import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { RestoreJobType, type InventoryResource, type Path, type RestoreJob } from '@repo/api-gw-sdk';
import type { ReactNode } from 'react';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { BasicLink } from '@/components/explorer/fileLink';
import { useDAL } from '@/contexts/useDAL';
import type { FilterProperty } from '@/types/advanceFilter';
import { parseGWSAttributes } from '@/utils/gwsAttributes';
import { googleWorkspaceDisplayPath, googleWorkspaceEntityLabel, googleWorkspaceExplorerHref, googleWorkspaceRestoreSourceLabel, parseGoogleWorkspaceRestoreItem, type GoogleWorkspaceEntity, type GoogleWorkspaceRestoreItem } from './googleWorkspaceRestorePath';
const googleWorkspaceRestoreItems = (job: RestoreJob): GoogleWorkspaceRestoreItem[] => {
  if (job.restoreType !== RestoreJobType.GoogleWorkspaceRestore) {
    return [];
  }
  return (job.partialRestoreData?.files?.files ?? []).map(file => parseGoogleWorkspaceRestoreItem(file.path, file.pathType)).filter((item): item is GoogleWorkspaceRestoreItem => !!item);
};

/**
 * Users and shared drives are named only through the attributes of the rows beneath them, so a
 * single row of the entity's own listing is enough; groups have no such rows and are looked up in
 * the directory index by id. Both are scoped to the restored snapshot, where the entity is
 * guaranteed to exist.
 */
const useGoogleWorkspaceEntity = (job: RestoreJob, item: GoogleWorkspaceRestoreItem | undefined): GoogleWorkspaceEntity | undefined => {
  const dal = useDAL();
  const resourceId = job.resourceDetails?.id ?? '';
  const snapshotId = job.snapshotDetails?.id;
  const nameFromAttributes = item?.workload.entityName;
  const directoryEntityType = item?.workload.directoryEntityType;
  const {
    data: listing
  } = dal.saasInventory.googleWorkspace.useListPath(nameFromAttributes ? resourceId : '', item?.entityPrefix ?? '', 1, snapshotId);
  const {
    data: directory
  } = dal.saasInventory.googleWorkspace.useDirectorySearch({
    resourceId: directoryEntityType ? resourceId : '',
    snapshotId,
    entityId: item?.entityId,
    entityType: directoryEntityType,
    pageIndex: 0,
    pageSize: 1
  });
  if (nameFromAttributes) {
    return {
      name: nameFromAttributes(parseGWSAttributes(listing?.data[0]?.snapshots?.[0]?.attributes))
    };
  }
  const record = directory?.data[0];
  return record && {
    name: record.name,
    email: record.email
  };
};
const GoogleWorkspaceRestoreSourceLine = t0 => {
  const $ = _c(5);
  const {
    job,
    item
  } = t0;
  const entity = useGoogleWorkspaceEntity(job, item);
  let t1;
  if ($[0] !== entity || $[1] !== item) {
    t1 = googleWorkspaceRestoreSourceLabel(item, googleWorkspaceEntityLabel(entity));
    $[0] = entity;
    $[1] = item;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <Typography>{t1}</Typography>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/** Links the restored item into the Workspace explorer, labelled by its display path. */
export const GoogleWorkspaceRestorePathLink = t0 => {
  const $ = _c(18);
  const {
    job,
    resource,
    file
  } = t0;
  let t1;
  if ($[0] !== file.path || $[1] !== file.pathType) {
    t1 = parseGoogleWorkspaceRestoreItem(file.path, file.pathType);
    $[0] = file.path;
    $[1] = file.pathType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const item = t1;
  const entity = useGoogleWorkspaceEntity(job, item);
  const t2 = file.path;
  let t3;
  if ($[3] !== entity || $[4] !== file.path) {
    t3 = googleWorkspaceDisplayPath(file.path, googleWorkspaceEntityLabel(entity));
    $[3] = entity;
    $[4] = file.path;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = job.snapshotDetails?.id;
  let t5;
  if ($[6] !== entity || $[7] !== file || $[8] !== resource.id || $[9] !== t4) {
    t5 = googleWorkspaceExplorerHref(resource.id, file, t4, entity);
    $[6] = entity;
    $[7] = file;
    $[8] = resource.id;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== resource || $[12] !== t3 || $[13] !== t5) {
    t6 = <BasicLink entity={resource} label={t3} href={t5} />;
    $[11] = resource;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== file.path || $[16] !== t6) {
    t7 = <CopyToClipboardWrapper textToCopy={t2}>{t6}</CopyToClipboardWrapper>;
    $[15] = file.path;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};

// useFields keeps a row when the element its Component returns is truthy, so an empty value must
// resolve to null in the Component itself rather than inside a child that renders null later.
const restoreLines = (lines: ReactNode[]) => {
  if (!lines.length) {
    return null;
  }
  return <Stack sx={{
    gap: '8px'
  }}>
      {lines}
    </Stack>;
};
export const googleWorkspaceRestoreSource: FilterProperty<RestoreJob> = {
  name: 'googleWorkspaceRestoreSource',
  displayName: 'Restored from',
  group: '',
  field: {
    Component: ({
      entity
    }) => {
      const sourceByEntity = new Map(googleWorkspaceRestoreItems(entity).map(item => [item.entityPrefix, item]));
      return restoreLines([...sourceByEntity.values()].map(item => <GoogleWorkspaceRestoreSourceLine key={item.entityPrefix} job={entity} item={item} />));
    }
  }
};
export const googleWorkspaceRestoreItem: FilterProperty<RestoreJob> = {
  name: 'googleWorkspaceRestoreItem',
  displayName: 'Restored item',
  group: '',
  field: {
    Component: ({
      entity
    }) => restoreLines(googleWorkspaceRestoreItems(entity).map((item, index) => <Typography key={`${index}-${item.item}`}>{item.item}</Typography>))
  }
};